import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Paper } from '@thirdspacelearning/library';

import {
    SessionNextLessonNotAvailable,
    SessionDone,
    SessionProgrammeNotAssigned,
    SessionPlannedToday,
    SessionResume,
    SessionStart,
    SessionIntroLO,
    SessionStartLesson,
} from './session-states';

import {
    currSessionPlannedAtSelector,
    currSessionPlannedStartNextSelector,
    currSessionStartingSelector,
    nextLessonSelector,
} from '@store/session';
import {
    currentProgrammeSelector,
    programmeSubjectDirectoriesSelector,
} from '@store/programme';
import { useIntroLoCompleted } from '@hooks';

const sx = {
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '125px',
        backgroundColor: 'background.paper',
    },
};

const SessionPanels = () => {
    const [searchParams] = useSearchParams();

    const nextLesson = useSelector(nextLessonSelector);
    const currentProgramme = useSelector(currentProgrammeSelector);
    const showStartSession = useSelector(currSessionStartingSelector);
    const sessionPlannedAt = useSelector(currSessionPlannedAtSelector);
    const sessionStartNext = useSelector(currSessionPlannedStartNextSelector);
    const introLoCompleted = useIntroLoCompleted();

    const { showResumeLessonOnStudentExperience: showResumeLesson } =
        useFlags();
    const subjectDirectories = useSelector(programmeSubjectDirectoriesSelector);

    const adHocLessons = useMemo(() => {
        if (!subjectDirectories) return null;

        const startedLessons = subjectDirectories.filter((dir) => dir.started);
        const notStartedLessons = subjectDirectories.filter(
            (dir) => !dir.started,
        );
        const uncompletedLessons = startedLessons.filter(
            (dir) => !dir.completed_at,
        );

        const data = {
            next:
                notStartedLessons.length === 0
                    ? subjectDirectories[0]
                    : notStartedLessons[0],
            resume: uncompletedLessons[uncompletedLessons.length - 1] || null,
        };

        return data;
    }, [subjectDirectories]);

    const sessionDone = Boolean(searchParams.get('sessionDone'));

    const renderPanel = () => {
        if (!currentProgramme) {
            return <SessionProgrammeNotAssigned />;
        } else if (sessionDone && nextLesson) {
            return <SessionDone sessionPlannedAt={sessionPlannedAt} />;
        } else if (!nextLesson) {
            return <SessionNextLessonNotAvailable />;
        } else if (showStartSession) {
            return <SessionStart />;
        } else if (sessionStartNext) {
            return <SessionPlannedToday sessionPlannedAt={sessionPlannedAt} />;
        } else if (!introLoCompleted) {
            return <SessionIntroLO />;
        } else if (showResumeLesson && adHocLessons?.resume) {
            return <SessionResume lastStartedLO={adHocLessons.resume} />;
        } else if (showResumeLesson && adHocLessons?.next) {
            return <SessionStartLesson lesson={adHocLessons.next} />;
        } else {
            return null;
        }
    };

    const panel = renderPanel();

    return (
        <Paper
            sx={{
                ...sx.wrapper,
                backgroundColor: 'background.paper',
            }}
            variant="outlined"
        >
            {panel}
        </Paper>
    );
};

export default SessionPanels;
